import {Document, Font} from '@react-pdf/renderer';
import { useOfferOverviewActions } from 'modules/leasingOfferOverviewPage/components/offerOverviewTab/useOfferOverviewActions';
import { LeasingOfferDetails, LeasingType } from 'modules/types/LeasingOffer';
import {
  PdfDetails,
  PdfDetailsRow,
  PdfParagraph,
  PdfSectionTitle,
  PdfStandardPage,
  PdfTable,
  PdfTableCell,
  PdfTableHeader,
  PdfTableRow,
  PdfText,
  PdfTitleContainer,
} from 'modules/ui/pdf';
import { PdfHeaderLogo } from 'modules/ui/pdf/PdfLogo';
import { useTranslation } from 'react-i18next';
import {
  unicreditRegularFontData,
  unicreditBoldFontData,
} from 'modules/ui/font/unicredit/unicreditFontData';

export interface LeasingOfferPdfProps {
  leasingOfferDetails?: LeasingOfferDetails;
}

Font.register({
  family: 'UniCredit',
  fonts: [
    { src: unicreditRegularFontData },
    { src: unicreditBoldFontData, fontWeight: 'bold' },
  ],
});

export const LeasingOfferPdf: React.FC<LeasingOfferPdfProps> = ({
  leasingOfferDetails,
}) => {
  const { t } = useTranslation();

  const {
    titleText,
    leasingObjectDetails,
    overviewTableColumns,
    offerOverviewTableObjects,
    leasingPaymentDetails,
    overallLeasingPaymentDetails,
    offerContentData,
    consultantDetails,
  } = useOfferOverviewActions(leasingOfferDetails);

  return (
    <Document>
      <PdfStandardPage>
        <PdfHeaderLogo />

        <PdfTitleContainer isLeasingTypeTitle>
          <PdfText isLeasingTypeTitle>{titleText.leasingTypeTitle}</PdfText>
        </PdfTitleContainer>

        <PdfTitleContainer>
          <PdfSectionTitle>
            <PdfText isUpperCase>
              {t(
                'leasingOfferOverviewPage.offerOverviewTab.informativeCalculationTitlePdf',
              )}
            </PdfText>
          </PdfSectionTitle>
        </PdfTitleContainer>

        <PdfDetails>
          {leasingObjectDetails.map((item) => (
            <PdfDetailsRow key={item.key} label={item.key} value={item.value} />
          ))}
        </PdfDetails>

        <PdfTable>
          <PdfTableHeader>
            <PdfTableRow>
              <PdfTableCell
                text={t(
                  'leasingOfferOverviewPage.offerOverviewTab.overviewTable.leasingObject',
                )}
                isAccented
              />
            </PdfTableRow>

            <PdfTableRow>
              {overviewTableColumns.map((column, index) => (
                <PdfTableCell
                  key={column.accessor}
                  isFirstColumn={index === 0}
                  isNarrowColumn={
                    column.accessor === 'currency' ||
                    column.accessor === 'percentage'
                  }
                  text={column.label}
                />
              ))}
            </PdfTableRow>
          </PdfTableHeader>
          {offerOverviewTableObjects.map((row) => (
            <PdfTableRow key={row.name}>
              <PdfTableCell isFirstColumn text={row.name} />
              <PdfTableCell isNarrowColumn text={row.currency} />
              <PdfTableCell isNarrowColumn text={row.percentage} />
              <PdfTableCell text={row.total} />
              <PdfTableCell text={row.pdv} />
              <PdfTableCell text={row.amountWithoutPdv} />
            </PdfTableRow>
          ))}
        </PdfTable>

        <PdfSectionTitle>
          {t('leasingOfferOverviewPage.offerOverviewTab.offerTitle')}
        </PdfSectionTitle>

        <PdfDetails>
          {leasingPaymentDetails.map((item) => (
            <PdfDetailsRow
              key={item.key}
              label={item.key}
              value={item.value}
              accented={item.accented}
            />
          ))}
        </PdfDetails>

        <PdfDetails>
          {overallLeasingPaymentDetails.map((item) => (
              <PdfDetailsRow
                  key={item.key}
                  label={item.key}
                  value={item.value}
                  accented={item.accented}
              />
          ))}
        </PdfDetails>

        {offerContentData.map((item) => (
          <PdfParagraph key={item.key} isOneLineText>
            {item.value}
          </PdfParagraph>
        ))}
      </PdfStandardPage>
    </Document>
  );
};
